import { useEffect, useId, useState } from 'react'
import { Globe, Plus, X } from 'lucide-react'
import type { Organization, CreateOrganizationRequest, OrganizationAddress } from '@shared/types'
import { cn } from '@/utils/cn'
import { validateSocialProfileUrl } from '@/utils/socialProfileUrl'
import { DsIsland } from '@/design-system/island'
import { Button, Dialog, Field, Input, Textarea } from '@/design-system/primitives'
/* The section rail is `ds-snav`, which is declared in settings.css. Four of
   the five pages that open this form never import that file. */
import '@/design-system/settings.css'
import '@/design-system/form.css'

interface OrganizationFormProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (data: CreateOrganizationRequest) => Promise<void>
  organization?: Organization | null
  isLoading?: boolean
  /** Pre-fill associated domains for new organizations (domain settings context) */
  defaultDomain?: string
  /**
   * Starting values for a new profile, when something already knows a few
   * facts (the first-week guide has the scan's name and description). Unlike
   * `organization`, this does not make the form an edit: it still creates.
   * Pass a stable object; a fresh literal on every render resets the form.
   */
  initialValues?: Partial<CreateOrganizationRequest>
}

type FormSection = 'identity' | 'contactLocation' | 'profiles' | 'domains'

/**
 * The sections, in the order a person fills them in. They are destinations in
 * a rail rather than four collapsible headers: a profile is twenty-odd fields,
 * and a header that can be shut is a place for a validation error to hide.
 */
const SECTIONS: { id: FormSection; label: string; lede: string }[] = [
  {
    id: 'identity',
    label: 'Business Identity',
    lede: 'What this business is called, and what it does.'
  },
  {
    id: 'contactLocation',
    label: 'Contact & Location',
    lede: 'How someone reaches this business, and where it operates.'
  },
  {
    id: 'profiles',
    label: 'Official Profiles & Links',
    lede: 'Add official profiles AI systems can use to disambiguate this business.'
  },
  {
    id: 'domains',
    label: 'Associated Domains',
    lede: 'Schema generation uses this Business Profile only when the page belongs to one of these domains or subdomains.'
  }
]

/** Which section a person has to be looking at to see and fix each error. */
const SECTION_OF_ERROR: Record<string, FormSection> = {
  name: 'identity',
  legalName: 'identity',
  url: 'identity',
  logoUrl: 'identity',
  description: 'identity',
  primaryCategory: 'identity',
  priorityTopics: 'identity',
  contactPageUrl: 'contactLocation',
  email: 'contactLocation',
  sameAs: 'profiles'
}

const MAX_PRIORITY_TOPICS = 10
const MAX_PRIORITY_TOPIC_LENGTH = 100

export default function OrganizationForm({
  isOpen,
  onClose,
  onSubmit,
  organization,
  isLoading = false,
  defaultDomain,
  initialValues
}: OrganizationFormProps) {
  const isEdit = !!organization
  /* The submit button lives in the dialog's action bar, outside the form. */
  const formId = useId()

  // Form state
  const [name, setName] = useState('')
  const [legalName, setLegalName] = useState('')
  const [url, setUrl] = useState('')
  const [logoUrl, setLogoUrl] = useState('')
  const [description, setDescription] = useState('')
  const [primaryCategory, setPrimaryCategory] = useState('')
  const [alternateNames, setAlternateNames] = useState<string[]>([])
  const [priorityTopics, setPriorityTopics] = useState<string[]>([])
  const [contactPageUrl, setContactPageUrl] = useState('')
  const [serviceArea, setServiceArea] = useState('')
  const [hidePublicAddress, setHidePublicAddress] = useState(false)
  const [streetAddress, setStreetAddress] = useState('')
  const [addressLocality, setAddressLocality] = useState('')
  const [addressRegion, setAddressRegion] = useState('')
  const [postalCode, setPostalCode] = useState('')
  const [addressCountry, setAddressCountry] = useState('')
  const [telephone, setTelephone] = useState('')
  const [email, setEmail] = useState('')
  const [sameAs, setSameAs] = useState<string[]>([])
  const [associatedDomains, setAssociatedDomains] = useState<string[]>([])
  const [isDefault, setIsDefault] = useState(false)

  // UI state
  const [activeSection, setActiveSection] = useState<FormSection>('identity')
  const [newAlternateName, setNewAlternateName] = useState('')
  const [newPriorityTopic, setNewPriorityTopic] = useState('')
  const [newSocialUrl, setNewSocialUrl] = useState('')
  const [newDomain, setNewDomain] = useState('')
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [logoPreviewError, setLogoPreviewError] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false) // Fix #35: double-submit prevention

  // Effect 1: Populate form when organization changes (edit mode)
  useEffect(() => {
    if (organization) {
      setName(organization.name || '')
      setLegalName(organization.legalName || '')
      setUrl(organization.url || '')
      setLogoUrl(organization.logoUrl || '')
      setDescription(organization.description || '')
      setPrimaryCategory(organization.primaryCategory || '')
      setAlternateNames(organization.alternateNames || [])
      setPriorityTopics(organization.priorityTopics || [])
      setContactPageUrl(organization.contactPageUrl || '')
      setServiceArea(organization.serviceArea || '')
      setHidePublicAddress(organization.hidePublicAddress || false)
      setStreetAddress(organization.address?.streetAddress || '')
      setAddressLocality(organization.address?.addressLocality || '')
      setAddressRegion(organization.address?.addressRegion || '')
      setPostalCode(organization.address?.postalCode || '')
      setAddressCountry(organization.address?.addressCountry || '')
      setTelephone(organization.telephone || '')
      setEmail(organization.email || '')
      setSameAs(organization.sameAs || [])
      setAssociatedDomains(organization.associatedDomains || [])
      setIsDefault(organization.isDefault || false)
      setActiveSection('identity')
    } else {
      // Reset form for new organization, from whatever is already known
      const seed = initialValues ?? {}
      setName(seed.name || '')
      setLegalName(seed.legalName || '')
      setUrl(seed.url || '')
      setLogoUrl(seed.logoUrl || '')
      setDescription(seed.description || '')
      setPrimaryCategory(seed.primaryCategory || '')
      setAlternateNames(seed.alternateNames || [])
      setPriorityTopics(seed.priorityTopics || [])
      setContactPageUrl(seed.contactPageUrl || '')
      setServiceArea(seed.serviceArea || '')
      setHidePublicAddress(seed.hidePublicAddress || false)
      setStreetAddress(seed.address?.streetAddress || '')
      setAddressLocality(seed.address?.addressLocality || '')
      setAddressRegion(seed.address?.addressRegion || '')
      setPostalCode(seed.address?.postalCode || '')
      setAddressCountry(seed.address?.addressCountry || '')
      setTelephone(seed.telephone || '')
      setEmail(seed.email || '')
      setSameAs(seed.sameAs || [])
      // Pre-fill domain when creating from domain settings context
      setAssociatedDomains(seed.associatedDomains || (defaultDomain ? [defaultDomain] : []))
      setActiveSection('identity')
      setIsDefault(seed.isDefault || false)
    }
  }, [organization, defaultDomain, initialValues])

  // Effect 2: Clear errors when form opens (separate concern)
  useEffect(() => {
    if (isOpen) {
      setErrors({})
    }
  }, [isOpen])

  const addAlternateName = () => {
    const trimmed = newAlternateName.trim()
    if (trimmed && !alternateNames.includes(trimmed)) {
      setAlternateNames([...alternateNames, trimmed])
      setNewAlternateName('')
    }
  }

  const removeAlternateName = (nameToRemove: string) => {
    setAlternateNames(alternateNames.filter(item => item !== nameToRemove))
  }

  const addPriorityTopic = () => {
    const trimmed = newPriorityTopic.trim()

    if (!trimmed) return

    if (priorityTopics.length >= MAX_PRIORITY_TOPICS) {
      setErrors(prev => ({
        ...prev,
        priorityTopics: 'You can add up to 10 priority topics.'
      }))
      return
    }

    if (trimmed.length > MAX_PRIORITY_TOPIC_LENGTH) {
      setErrors(prev => ({
        ...prev,
        priorityTopics: `Each priority topic must be ${MAX_PRIORITY_TOPIC_LENGTH} characters or less.`
      }))
      return
    }

    if (!priorityTopics.includes(trimmed)) {
      setPriorityTopics([...priorityTopics, trimmed])
      setNewPriorityTopic('')
      setErrors(prev => {
        const next = { ...prev }
        delete next.priorityTopics
        return next
      })
    }
  }

  const removePriorityTopic = (topicToRemove: string) => {
    setPriorityTopics(priorityTopics.filter(item => item !== topicToRemove))
    setErrors(prev => {
      const next = { ...prev }
      delete next.priorityTopics
      return next
    })
  }

  /**
   * Validated here rather than left to the save, because the server rejects a
   * link that cannot resolve to a profile and a late 400 makes the customer
   * hunt for which of their links was the problem. Same rule as the server, so
   * anything this accepts saves.
   */
  const addSocialUrl = () => {
    const trimmed = newSocialUrl.trim()
    if (!trimmed) return

    const result = validateSocialProfileUrl(trimmed)
    if (!result.ok) {
      setErrors(prev => ({ ...prev, sameAs: result.message }))
      return
    }

    if (!sameAs.includes(result.normalized)) {
      setSameAs([...sameAs, result.normalized])
    }
    setNewSocialUrl('')
    setErrors(prev => {
      const next = { ...prev }
      delete next.sameAs
      return next
    })
  }

  const removeSocialUrl = (urlToRemove: string) => {
    setSameAs(sameAs.filter(u => u !== urlToRemove))
    setErrors(prev => {
      const next = { ...prev }
      delete next.sameAs
      return next
    })
  }

  /**
   * Normalize domain input - handles URLs, www prefixes, ports, and paths
   */
  const normalizeDomain = (input: string): string => {
    let domain = input.trim().toLowerCase()

    // Try to parse as URL first to extract hostname properly
    try {
      const url = domain.startsWith('http') ? domain : `https://${domain}`
      const parsed = new URL(url)
      domain = parsed.hostname
    } catch {
      // Not a valid URL, continue with string processing
    }

    // Remove www. prefix
    domain = domain.replace(/^www\./, '')

    // Remove any trailing slashes or paths
    domain = domain.split('/')[0]

    // Remove port if present (unless it's a wildcard domain)
    if (!domain.startsWith('*.')) {
      domain = domain.split(':')[0]
    }

    return domain
  }

  const addDomain = () => {
    const normalized = normalizeDomain(newDomain)
    if (normalized && !associatedDomains.includes(normalized)) {
      setAssociatedDomains([...associatedDomains, normalized])
      setNewDomain('')
    }
  }

  const removeDomain = (domainToRemove: string) => {
    setAssociatedDomains(associatedDomains.filter(d => d !== domainToRemove))
  }

  /**
   * Validate URL using URL constructor for robust validation
   */
  const isValidUrl = (urlString: string): boolean => {
    try {
      const parsed = new URL(urlString)
      return parsed.protocol === 'http:' || parsed.protocol === 'https:'
    } catch {
      return false
    }
  }

  /**
   * Returns what is wrong rather than just whether anything is, so the save
   * can take the person to the section holding the first problem. Pressing
   * Save and having nothing happen, because the bad field was in a section
   * that was not on screen, is the failure this replaces.
   */
  const validate = (): Record<string, string> => {
    const newErrors: Record<string, string> = {}

    // Name validation
    if (!name.trim()) {
      newErrors.name = 'Business display name is required'
    } else if (name.trim().length > 200) {
      newErrors.name = 'Business display name must be 200 characters or less'
    }

    if (legalName.trim().length > 200) {
      newErrors.legalName = 'Legal name must be 200 characters or less'
    }

    if (description.trim().length > 2000) {
      newErrors.description = 'Description must be 2,000 characters or less'
    }

    if (primaryCategory.trim().length > 120) {
      newErrors.primaryCategory = 'Primary category must be 120 characters or less'
    }

    // Email validation with length check
    if (email) {
      if (email.length > 254) {
        newErrors.email = 'Email address is too long'
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) {
        newErrors.email = 'Please enter a valid email address'
      }
    }

    // URL validation using URL constructor
    if (url) {
      if (!isValidUrl(url)) {
        newErrors.url = 'Please enter a valid URL (must start with http:// or https://)'
      }
    }

    // Logo URL validation
    if (logoUrl) {
      if (!isValidUrl(logoUrl)) {
        newErrors.logoUrl = 'Please enter a valid logo URL (must start with http:// or https://)'
      }
    }

    if (contactPageUrl) {
      if (!isValidUrl(contactPageUrl)) {
        newErrors.contactPageUrl = 'Please enter a valid contact page URL'
      }
    }

    if (priorityTopics.length > MAX_PRIORITY_TOPICS) {
      newErrors.priorityTopics = 'You can add up to 10 priority topics.'
    } else {
      const overlongTopic = priorityTopics.find(topic => topic.length > MAX_PRIORITY_TOPIC_LENGTH)
      if (overlongTopic) {
        newErrors.priorityTopics = `Each priority topic must be ${MAX_PRIORITY_TOPIC_LENGTH} characters or less.`
      }
    }

    setErrors(newErrors)
    return newErrors
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // Fix #35: Prevent double-submit
    if (isSubmitting || isLoading) return

    const found = validate()
    if (Object.keys(found).length > 0) {
      const firstBad = SECTIONS.find(section =>
        Object.keys(found).some(key => SECTION_OF_ERROR[key] === section.id)
      )
      if (firstBad) setActiveSection(firstBad.id)
      return
    }

    // For edit mode, send empty strings for cleared fields so backend sets them to null
    // For create mode, use undefined to let backend use defaults
    const address: OrganizationAddress | undefined =
      streetAddress || addressLocality || addressRegion || postalCode || addressCountry || isEdit
        ? {
            streetAddress: streetAddress.trim() || (isEdit ? '' : undefined),
            addressLocality: addressLocality.trim() || (isEdit ? '' : undefined),
            addressRegion: addressRegion.trim() || (isEdit ? '' : undefined),
            postalCode: postalCode.trim() || (isEdit ? '' : undefined),
            addressCountry: addressCountry.trim() || (isEdit ? '' : undefined)
          }
        : undefined

    const data: CreateOrganizationRequest = {
      name: name.trim(),
      legalName: legalName.trim() || (isEdit ? '' : undefined),
      // For edit mode: send empty string for cleared fields (backend converts to null)
      // For create mode: send undefined to skip the field
      url: url.trim() || (isEdit ? '' : undefined),
      logoUrl: logoUrl.trim() || (isEdit ? '' : undefined),
      description: description.trim() || (isEdit ? '' : undefined),
      primaryCategory: primaryCategory.trim() || (isEdit ? '' : undefined),
      alternateNames,
      contactPageUrl: contactPageUrl.trim() || (isEdit ? '' : undefined),
      serviceArea: serviceArea.trim() || (isEdit ? '' : undefined),
      hidePublicAddress,
      priorityTopics,
      address,
      telephone: telephone.trim() || (isEdit ? '' : undefined),
      email: email.trim() || (isEdit ? '' : undefined),
      // Always send arrays for update operations so backend knows to update them
      // For create, empty arrays are fine (backend defaults to [])
      sameAs: sameAs,
      associatedDomains: associatedDomains,
      isDefault
    }

    // Fix #35: Set submitting state to prevent double-submit
    setIsSubmitting(true)
    try {
      await onSubmit(data)
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!isOpen) return null

  const active = SECTIONS.find(section => section.id === activeSection) ?? SECTIONS[0]
  const sectionsWithErrors = new Set<FormSection>(
    Object.keys(errors)
      .map(key => SECTION_OF_ERROR[key])
      .filter((section): section is FormSection => Boolean(section))
  )
  const busy = isLoading || isSubmitting

  return (
    <DsIsland panel={false} ground={false}>
      <Dialog
        open
        size="xl"
        onClose={onClose}
        title={isEdit ? 'Edit Business Profile' : 'Create Business Profile'}
        lead="These facts are reused when SuperSchema generates schema for your connected domains."
        actions={
          <>
            <Button variant="secondary" onClick={onClose} disabled={busy}>
              Cancel
            </Button>
            <Button type="submit" form={formId} loading={busy}>
              {isEdit ? 'Save Business Profile' : 'Create Business Profile'}
            </Button>
          </>
        }
      >
        <form id={formId} onSubmit={handleSubmit} className="ds-formdialog">
          <div className="ds-formdialog__rail">
            <ul className="ds-snav" aria-label="Business Profile sections">
              {SECTIONS.map(section => (
                <li key={section.id}>
                  <button
                    type="button"
                    aria-current={section.id === activeSection || undefined}
                    onClick={() => setActiveSection(section.id)}
                  >
                    {section.label}
                    {sectionsWithErrors.has(section.id) && (
                      <span className="ds-snav__flag" role="img" aria-label="Something here needs fixing" />
                    )}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div className="ds-formdialog__panel">
            <p className="ds-formdialog__lede">{active.lede}</p>

            {activeSection === 'identity' && (
              <div className="ds-fields">
                <Field label="Business display name (required)" error={errors.name}>
                  {(id, describedBy) => (
                    <Input
                      id={id}
                      aria-describedby={describedBy}
                      aria-required="true"
                      aria-invalid={errors.name ? true : undefined}
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Super Corp"
                      maxLength={200}
                    />
                  )}
                </Field>

                <Field label="Legal business name" error={errors.legalName}>
                  {(id, describedBy) => (
                    <Input
                      id={id}
                      aria-describedby={describedBy}
                      aria-invalid={errors.legalName ? true : undefined}
                      value={legalName}
                      onChange={(e) => setLegalName(e.target.value)}
                      placeholder="Super Corp, Inc."
                      maxLength={200}
                    />
                  )}
                </Field>

                <Field label="Website URL" error={errors.url}>
                  {(id, describedBy) => (
                    <Input
                      id={id}
                      type="url"
                      aria-describedby={describedBy}
                      aria-invalid={errors.url ? true : undefined}
                      value={url}
                      onChange={(e) => setUrl(e.target.value)}
                      placeholder="https://acme.com"
                    />
                  )}
                </Field>

                <Field label="Logo URL" error={errors.logoUrl}>
                  {(id, describedBy) => (
                    <>
                      <Input
                        id={id}
                        type="url"
                        aria-describedby={describedBy}
                        aria-invalid={errors.logoUrl ? true : undefined}
                        value={logoUrl}
                        onChange={(e) => {
                          setLogoUrl(e.target.value)
                          setLogoPreviewError(false)
                        }}
                        placeholder="https://acme.com/logo.png"
                      />
                      {logoUrl && isValidUrl(logoUrl) && (
                        <div className="ds-logopeek">
                          {!logoPreviewError ? (
                            <img src={logoUrl} alt="Logo preview" onError={() => setLogoPreviewError(true)} />
                          ) : (
                            <span>Unable to load image preview</span>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </Field>

                <Field
                  label="Short description"
                  hint="Used as additive context in the publisher Organization schema."
                  error={errors.description}
                  aside={`${description.length}/2000`}
                >
                  {(id, describedBy) => (
                    <>
                      <Textarea
                        id={id}
                        aria-describedby={describedBy}
                        aria-invalid={errors.description ? true : undefined}
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        placeholder="What the business does, who it serves, and what AI systems should understand."
                        maxLength={2000}
                        rows={4}
                      />
                    </>
                  )}
                </Field>

                <Field label="Primary category" error={errors.primaryCategory}>
                  {(id, describedBy) => (
                    <Input
                      id={id}
                      aria-describedby={describedBy}
                      aria-invalid={errors.primaryCategory ? true : undefined}
                      value={primaryCategory}
                      onChange={(e) => setPrimaryCategory(e.target.value)}
                      placeholder="Solar technology"
                      maxLength={120}
                    />
                  )}
                </Field>

                <Field label="Alternate names" hint="Press Enter to add. Select one to remove it.">
                  {(id, describedBy) => (
                    <>
                      {alternateNames.length > 0 && (
                        <div className="ds-tokens">
                          {alternateNames.map((alternateName) => (
                            <button
                              key={alternateName}
                              type="button"
                              className="ds-token"
                              aria-label={`Remove ${alternateName}`}
                              onClick={() => removeAlternateName(alternateName)}
                            >
                              <span>{alternateName}</span>
                              <X aria-hidden="true" />
                            </button>
                          ))}
                        </div>
                      )}
                      <div className="ds-addrow">
                        <Input
                          id={id}
                          aria-describedby={describedBy}
                          value={newAlternateName}
                          onChange={(e) => setNewAlternateName(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault()
                              addAlternateName()
                            }
                          }}
                          placeholder="A common abbreviation, brand alias, or DBA"
                        />
                        <Button variant="secondary" icon aria-label="Add alternate name" onClick={addAlternateName}>
                          <Plus />
                        </Button>
                      </div>
                    </>
                  )}
                </Field>

                <Field
                  label="Priority topics"
                  hint="Add up to 10 topics AI systems should associate with this business."
                  error={errors.priorityTopics}
                  aside={
                    <span className={cn(priorityTopics.length >= MAX_PRIORITY_TOPICS && 'ds-field__aside--full')}>
                      {priorityTopics.length}/{MAX_PRIORITY_TOPICS}
                    </span>
                  }
                >
                  {(id, describedBy) => (
                    <>
                      {priorityTopics.length > 0 && (
                        <div className="ds-tokens">
                          {priorityTopics.map((topic) => (
                            <button
                              key={topic}
                              type="button"
                              className="ds-token"
                              aria-label={`Remove ${topic}`}
                              onClick={() => removePriorityTopic(topic)}
                            >
                              <span>{topic}</span>
                              <X aria-hidden="true" />
                            </button>
                          ))}
                        </div>
                      )}
                      <div className="ds-addrow">
                        <Input
                          id={id}
                          aria-describedby={describedBy}
                          aria-invalid={errors.priorityTopics ? true : undefined}
                          value={newPriorityTopic}
                          onChange={(e) => {
                            setNewPriorityTopic(e.target.value)
                            if (errors.priorityTopics && priorityTopics.length < MAX_PRIORITY_TOPICS) {
                              setErrors(prev => {
                                const next = { ...prev }
                                delete next.priorityTopics
                                return next
                              })
                            }
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault()
                              addPriorityTopic()
                            }
                          }}
                          placeholder="A topic this business should be known for"
                          maxLength={MAX_PRIORITY_TOPIC_LENGTH}
                        />
                        <Button
                          variant="secondary"
                          icon
                          aria-label="Add priority topic"
                          onClick={addPriorityTopic}
                          disabled={priorityTopics.length >= MAX_PRIORITY_TOPICS}
                          title={priorityTopics.length >= MAX_PRIORITY_TOPICS ? 'You can add up to 10 priority topics.' : 'Add priority topic'}
                        >
                          <Plus />
                        </Button>
                      </div>
                    </>
                  )}
                </Field>
              </div>
            )}

            {activeSection === 'contactLocation' && (
              <div className="ds-fields">
                <Field label="Contact page URL" error={errors.contactPageUrl}>
                  {(id, describedBy) => (
                    <Input
                      id={id}
                      type="url"
                      aria-describedby={describedBy}
                      aria-invalid={errors.contactPageUrl ? true : undefined}
                      value={contactPageUrl}
                      onChange={(e) => setContactPageUrl(e.target.value)}
                      placeholder="https://acme.com/contact"
                    />
                  )}
                </Field>

                <div className="ds-fields ds-fields--two">
                  <Field label="Email address" error={errors.email}>
                    {(id, describedBy) => (
                      <Input
                        id={id}
                        type="email"
                        aria-describedby={describedBy}
                        aria-invalid={errors.email ? true : undefined}
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="contact@acme.com"
                      />
                    )}
                  </Field>
                  <Field label="Phone number">
                    {(id) => (
                      <Input
                        id={id}
                        type="tel"
                        value={telephone}
                        onChange={(e) => setTelephone(e.target.value)}
                        placeholder="+1 (555) 123-4567"
                        maxLength={30}
                      />
                    )}
                  </Field>
                </div>

                <Field label="Service area">
                  {(id) => (
                    <Input
                      id={id}
                      value={serviceArea}
                      onChange={(e) => setServiceArea(e.target.value)}
                      placeholder="United States, North America, or Denver metro"
                      maxLength={500}
                    />
                  )}
                </Field>

                <Field label="Street address">
                  {(id) => (
                    <Input
                      id={id}
                      value={streetAddress}
                      onChange={(e) => setStreetAddress(e.target.value)}
                      placeholder="123 Main Street"
                      maxLength={500}
                    />
                  )}
                </Field>

                <div className="ds-fields ds-fields--two">
                  <Field label="City">
                    {(id) => (
                      <Input
                        id={id}
                        value={addressLocality}
                        onChange={(e) => setAddressLocality(e.target.value)}
                        placeholder="Austin"
                        maxLength={100}
                      />
                    )}
                  </Field>
                  <Field label="State or province">
                    {(id) => (
                      <Input
                        id={id}
                        value={addressRegion}
                        onChange={(e) => setAddressRegion(e.target.value)}
                        placeholder="TX"
                        maxLength={100}
                      />
                    )}
                  </Field>
                  <Field label="Postal code">
                    {(id) => (
                      <Input
                        id={id}
                        value={postalCode}
                        onChange={(e) => setPostalCode(e.target.value)}
                        placeholder="78701"
                        maxLength={20}
                      />
                    )}
                  </Field>
                  <Field label="Country">
                    {(id) => (
                      <Input
                        id={id}
                        value={addressCountry}
                        onChange={(e) => setAddressCountry(e.target.value)}
                        placeholder="US"
                        maxLength={100}
                      />
                    )}
                  </Field>
                </div>

                <label className="ds-optionbox ds-check">
                  <input
                    type="checkbox"
                    checked={hidePublicAddress}
                    onChange={(e) => setHidePublicAddress(e.target.checked)}
                  />
                  <span>
                    <span className="ds-optionbox__label">Hide public street address</span>
                    <span className="ds-optionbox__help">
                      SuperSchema can keep city, region, postal code, and country while omitting the street address from generated publisher schema.
                    </span>
                  </span>
                </label>
              </div>
            )}

            {activeSection === 'profiles' && (
              <Field
                label="Profile links"
                hint="Link the profile itself, not the site homepage."
                error={errors.sameAs}
              >
                {(id, describedBy) => (
                  <>
                    {sameAs.length > 0 && (
                      <div className="ds-list">
                        {sameAs.map((socialUrl) => (
                          <div key={socialUrl} className="ds-listrow">
                            <div className="ds-listrow__value">
                              <span>{socialUrl}</span>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              icon
                              aria-label={`Remove ${socialUrl}`}
                              onClick={() => removeSocialUrl(socialUrl)}
                            >
                              <X />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="ds-addrow">
                      <Input
                        id={id}
                        aria-describedby={describedBy}
                        value={newSocialUrl}
                        onChange={(e) => {
                          setNewSocialUrl(e.target.value)
                          if (errors.sameAs) {
                            setErrors(prev => {
                              const next = { ...prev }
                              delete next.sameAs
                              return next
                            })
                          }
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault()
                            addSocialUrl()
                          }
                        }}
                        placeholder="https://linkedin.com/company/acme"
                        aria-invalid={errors.sameAs ? true : undefined}
                      />
                      <Button variant="secondary" icon aria-label="Add profile link" onClick={addSocialUrl}>
                        <Plus />
                      </Button>
                    </div>
                  </>
                )}
              </Field>
            )}

            {activeSection === 'domains' && (
              <Field label="Domains" hint="Subdomains of anything listed here are covered too.">
                {(id, describedBy) => (
                  <>
                    {associatedDomains.length > 0 && (
                      <div className="ds-list">
                        {associatedDomains.map((domain) => (
                          <div key={domain} className="ds-listrow">
                            <div className="ds-listrow__value">
                              <Globe aria-hidden="true" />
                              <span>{domain}</span>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              icon
                              aria-label={`Remove ${domain}`}
                              onClick={() => removeDomain(domain)}
                            >
                              <X />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="ds-addrow">
                      <Input
                        id={id}
                        aria-describedby={describedBy}
                        value={newDomain}
                        onChange={(e) => setNewDomain(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault()
                            addDomain()
                          }
                        }}
                        placeholder="example.com"
                      />
                      <Button variant="secondary" icon aria-label="Add domain" onClick={addDomain}>
                        <Plus />
                      </Button>
                    </div>
                  </>
                )}
              </Field>
            )}

            <div className="ds-formdialog__foot">
              <label className="ds-optionbox ds-check">
                <input
                  type="checkbox"
                  checked={isDefault}
                  onChange={(e) => setIsDefault(e.target.checked)}
                />
                <span>
                  <span className="ds-optionbox__label">Mark as preferred Business Profile</span>
                  <span className="ds-optionbox__help">
                    Preferred profile in your account. Schema generation still requires an associated domain match.
                  </span>
                </span>
              </label>
            </div>
          </div>
        </form>
      </Dialog>
    </DsIsland>
  )
}
